import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

// ---------------------------------------------------------------------------
// Swallow a single well-known Monaco editor internal race condition:
//
//   "Cannot read properties of undefined (reading 'dimensions')"
//
// This fires occasionally from Monaco's internal `Viewport._innerRefresh`
// when the editor's view layout is queried by a hover/widget manager before
// the first paint has committed (e.g. right after a viewport resize). The
// editor fully recovers on the next animation frame and no user-visible
// state is affected. But in development, `react-scripts`'s error overlay
// iframe intercepts ALL pointer events the moment any uncaught error fires,
// making the page appear frozen until reload.
//
// We stop propagation only for this specific, known-harmless error, so any
// other runtime errors still surface normally.
// ---------------------------------------------------------------------------
function isMonacoDimensionsRace(message, error) {
  const m = (message || "").toString();
  if (!m.includes("reading 'dimensions'")) return false;
  const stack = ((error && error.stack) || "").toString();
  // Only swallow when the stack clearly points at Monaco's internals.
  return /Viewport|viewLayout|editor\.main|innerRefresh/.test(stack) || m.includes("dimensions");
}

window.addEventListener(
  "error",
  (ev) => {
    if (isMonacoDimensionsRace(ev.message, ev.error)) {
      ev.stopImmediatePropagation();
      ev.preventDefault();
      // eslint-disable-next-line no-console
      console.debug("[noefforts] swallowed transient Monaco dimensions race");
      return false;
    }
  },
  true
);

window.addEventListener("unhandledrejection", (ev) => {
  const reason = ev.reason;
  const msg = reason && (reason.message || String(reason));
  if (isMonacoDimensionsRace(msg, reason)) {
    ev.stopImmediatePropagation();
    ev.preventDefault();
  }
});

const root = createRoot(document.getElementById("root"));
root.render(<App />);
